/**
 * BYK-32: Enhanced Snap-to-Wall/Grid System
 *
 * Provides intelligent snapping for kitchen design elements:
 * - Wall proximity snapping (cabinets snap flush to walls)
 * - Corner snapping (prioritized for L-shaped layouts)
 * - Cabinet-to-cabinet magnetic snapping
 * - Alignment snapping (align with other objects)
 * - Grid snapping (fallback)
 */

import type {
  Point2D,
  WallSegment,
  PlacedVolume,
  ProductResponse,
  RoomConfig,
  SnapConfig,
  SnapResult,
  SnapTarget,
  SnapGuideLine,
  SnapTargetType,
} from "@byk/shared";
import { DEFAULT_SNAP_CONFIG, SNAP_PRIORITY } from "@byk/shared";

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Calculate distance between two points
 */
function distance(p1: Point2D, p2: Point2D): number {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
}

/**
 * Get the bounding box of a volume
 */
function getVolumeBounds(
  volume: PlacedVolume,
  products: ProductResponse[]
): { left: number; right: number; top: number; bottom: number } | null {
  const product = products.find((p) => p.id === volume.productId);
  if (!product?.dimensions) return null;

  const halfWidth = product.dimensions.width / 2;
  const halfDepth = product.dimensions.depth / 2;

  // Account for rotation (simplified - only 0, 90, 180, 270 degrees)
  const rotationRad = (volume.rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rotationRad));
  const sin = Math.abs(Math.sin(rotationRad));

  const effectiveHalfWidth = halfWidth * cos + halfDepth * sin;
  const effectiveHalfDepth = halfWidth * sin + halfDepth * cos;

  return {
    left: volume.position.x - effectiveHalfWidth,
    right: volume.position.x + effectiveHalfWidth,
    top: volume.position.y - effectiveHalfDepth,
    bottom: volume.position.y + effectiveHalfDepth,
  };
}

/**
 * Get the closest point on a line segment to a given point
 */
function closestPointOnSegment(
  point: Point2D,
  segStart: Point2D,
  segEnd: Point2D
): Point2D {
  const dx = segEnd.x - segStart.x;
  const dy = segEnd.y - segStart.y;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) return segStart;

  let t = ((point.x - segStart.x) * dx + (point.y - segStart.y) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));

  return {
    x: segStart.x + t * dx,
    y: segStart.y + t * dy,
  };
}

/**
 * Get the normal vector of a wall segment (pointing inward)
 */
function getWallNormal(wall: WallSegment): Point2D {
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length === 0) return { x: 0, y: 0 };

  // Perpendicular vector (rotated 90 degrees)
  return {
    x: -dy / length,
    y: dx / length,
  };
}

// =============================================================================
// Snap Detection Functions
// =============================================================================

/**
 * Find wall snap targets for a given position and object size
 */
function findWallSnapTargets(
  position: Point2D,
  objectWidth: number,
  objectDepth: number,
  walls: WallSegment[],
  threshold: number
): SnapTarget[] {
  const targets: SnapTarget[] = [];
  const halfWidth = objectWidth / 2;
  const halfDepth = objectDepth / 2;

  for (const wall of walls) {
    // Find closest point on wall to the object center
    const closestPoint = closestPointOnSegment(position, wall.start, wall.end);
    const dist = distance(position, closestPoint);

    // Check if within threshold (accounting for object size)
    const effectiveThreshold = threshold + Math.max(halfWidth, halfDepth);

    if (dist <= effectiveThreshold) {
      const normal = getWallNormal(wall);

      // Calculate snapped position (flush against wall, accounting for wall thickness)
      const wallOffset = wall.thickness / 2;
      const objectOffset = halfDepth; // Object's depth from center

      // Determine which side of the wall the object is on
      const toObject = {
        x: position.x - closestPoint.x,
        y: position.y - closestPoint.y,
      };
      const dotProduct = toObject.x * normal.x + toObject.y * normal.y;
      const side = dotProduct >= 0 ? 1 : -1;

      // Snap position: wall surface + object offset
      const snappedPosition: Point2D = {
        x: closestPoint.x + normal.x * (wallOffset + objectOffset) * side,
        y: closestPoint.y + normal.y * (wallOffset + objectOffset) * side,
      };

      const snapDistance = distance(position, snappedPosition);

      if (snapDistance <= threshold) {
        targets.push({
          type: "wall",
          position: snappedPosition,
          originalPosition: position,
          distance: snapDistance,
          targetId: wall.id,
          priority: SNAP_PRIORITY.wall,
        });
      }
    }
  }

  return targets;
}

/**
 * Find corner snap targets
 */
function findCornerSnapTargets(
  position: Point2D,
  objectWidth: number,
  objectDepth: number,
  room: RoomConfig,
  walls: WallSegment[],
  threshold: number
): SnapTarget[] {
  const targets: SnapTarget[] = [];
  const halfWidth = objectWidth / 2;
  const halfDepth = objectDepth / 2;

  // Room corners (with wall thickness offset)
  const wallThickness = walls.length > 0 ? walls[0].thickness : 15;
  const offset = wallThickness / 2;

  const corners: Array<{ position: Point2D; label: string }> = [
    {
      position: { x: offset + halfWidth, y: offset + halfDepth },
      label: "top-left",
    },
    {
      position: { x: room.width - offset - halfWidth, y: offset + halfDepth },
      label: "top-right",
    },
    {
      position: { x: offset + halfWidth, y: room.depth - offset - halfDepth },
      label: "bottom-left",
    },
    {
      position: {
        x: room.width - offset - halfWidth,
        y: room.depth - offset - halfDepth,
      },
      label: "bottom-right",
    },
  ];

  for (const corner of corners) {
    const dist = distance(position, corner.position);

    if (dist <= threshold) {
      targets.push({
        type: "corner",
        position: corner.position,
        originalPosition: position,
        distance: dist,
        targetId: corner.label,
        priority: SNAP_PRIORITY.corner,
      });
    }
  }

  return targets;
}

/**
 * Find cabinet-to-cabinet snap targets
 */
function findCabinetSnapTargets(
  position: Point2D,
  objectWidth: number,
  objectDepth: number,
  currentVolumeId: string | null,
  volumes: PlacedVolume[],
  products: ProductResponse[],
  threshold: number
): SnapTarget[] {
  const targets: SnapTarget[] = [];
  const halfWidth = objectWidth / 2;
  const halfDepth = objectDepth / 2;

  for (const volume of volumes) {
    // Skip self
    if (volume.id === currentVolumeId) continue;

    const bounds = getVolumeBounds(volume, products);
    if (!bounds) continue;

    // Check snap to each side of the existing volume
    const snapPositions: Array<{ pos: Point2D; axis: "x" | "y" }> = [
      // Snap to left side
      { pos: { x: bounds.left - halfWidth, y: position.y }, axis: "x" },
      // Snap to right side
      { pos: { x: bounds.right + halfWidth, y: position.y }, axis: "x" },
      // Snap to top side
      { pos: { x: position.x, y: bounds.top - halfDepth }, axis: "y" },
      // Snap to bottom side
      { pos: { x: position.x, y: bounds.bottom + halfDepth }, axis: "y" },
    ];

    for (const snap of snapPositions) {
      const dist = Math.abs(
        snap.axis === "x" ? position.x - snap.pos.x : position.y - snap.pos.y
      );

      // Also check if we're overlapping on the perpendicular axis
      const perpOverlap =
        snap.axis === "x"
          ? position.y - halfDepth < bounds.bottom &&
            position.y + halfDepth > bounds.top
          : position.x - halfWidth < bounds.right &&
            position.x + halfWidth > bounds.left;

      if (dist <= threshold && perpOverlap) {
        targets.push({
          type: "cabinet",
          position: snap.pos,
          originalPosition: position,
          distance: dist,
          targetId: volume.id,
          priority: SNAP_PRIORITY.cabinet,
        });
      }
    }
  }

  return targets;
}

/**
 * Find alignment snap targets (align edges with other objects)
 */
function findAlignmentSnapTargets(
  position: Point2D,
  objectWidth: number,
  objectDepth: number,
  currentVolumeId: string | null,
  volumes: PlacedVolume[],
  products: ProductResponse[],
  room: RoomConfig,
  threshold: number
): SnapTarget[] {
  const targets: SnapTarget[] = [];
  const halfWidth = objectWidth / 2;
  const halfDepth = objectDepth / 2;

  // Collect all edge positions to align with
  const xEdges: number[] = [0, room.width]; // Room edges
  const yEdges: number[] = [0, room.depth];

  for (const volume of volumes) {
    if (volume.id === currentVolumeId) continue;

    const bounds = getVolumeBounds(volume, products);
    if (!bounds) continue;

    xEdges.push(bounds.left, bounds.right, (bounds.left + bounds.right) / 2);
    yEdges.push(bounds.top, bounds.bottom, (bounds.top + bounds.bottom) / 2);
  }

  // Check alignment with X edges (left, right, center of current object)
  const currentLeft = position.x - halfWidth;
  const currentRight = position.x + halfWidth;
  const currentCenterX = position.x;

  for (const xEdge of xEdges) {
    // Align left edge
    const leftDist = Math.abs(currentLeft - xEdge);
    if (leftDist <= threshold && leftDist > 0) {
      targets.push({
        type: "alignment",
        position: { x: xEdge + halfWidth, y: position.y },
        originalPosition: position,
        distance: leftDist,
        priority: SNAP_PRIORITY.alignment,
      });
    }

    // Align right edge
    const rightDist = Math.abs(currentRight - xEdge);
    if (rightDist <= threshold && rightDist > 0) {
      targets.push({
        type: "alignment",
        position: { x: xEdge - halfWidth, y: position.y },
        originalPosition: position,
        distance: rightDist,
        priority: SNAP_PRIORITY.alignment,
      });
    }

    // Align center
    const centerDist = Math.abs(currentCenterX - xEdge);
    if (centerDist <= threshold && centerDist > 0) {
      targets.push({
        type: "alignment",
        position: { x: xEdge, y: position.y },
        originalPosition: position,
        distance: centerDist,
        priority: SNAP_PRIORITY.alignment,
      });
    }
  }

  // Check alignment with Y edges
  const currentTop = position.y - halfDepth;
  const currentBottom = position.y + halfDepth;
  const currentCenterY = position.y;

  for (const yEdge of yEdges) {
    // Align top edge
    const topDist = Math.abs(currentTop - yEdge);
    if (topDist <= threshold && topDist > 0) {
      targets.push({
        type: "alignment",
        position: { x: position.x, y: yEdge + halfDepth },
        originalPosition: position,
        distance: topDist,
        priority: SNAP_PRIORITY.alignment,
      });
    }

    // Align bottom edge
    const bottomDist = Math.abs(currentBottom - yEdge);
    if (bottomDist <= threshold && bottomDist > 0) {
      targets.push({
        type: "alignment",
        position: { x: position.x, y: yEdge - halfDepth },
        originalPosition: position,
        distance: bottomDist,
        priority: SNAP_PRIORITY.alignment,
      });
    }

    // Align center
    const centerDistY = Math.abs(currentCenterY - yEdge);
    if (centerDistY <= threshold && centerDistY > 0) {
      targets.push({
        type: "alignment",
        position: { x: position.x, y: yEdge },
        originalPosition: position,
        distance: centerDistY,
        priority: SNAP_PRIORITY.alignment,
      });
    }
  }

  return targets;
}

/**
 * Find grid snap target
 */
function findGridSnapTarget(
  position: Point2D,
  gridSize: number
): SnapTarget {
  const snappedPosition: Point2D = {
    x: Math.round(position.x / gridSize) * gridSize,
    y: Math.round(position.y / gridSize) * gridSize,
  };

  return {
    type: "grid",
    position: snappedPosition,
    originalPosition: position,
    distance: distance(position, snappedPosition),
    priority: SNAP_PRIORITY.grid,
  };
}

// =============================================================================
// Guide Line Generation
// =============================================================================

/**
 * Generate visual guide lines for snap feedback
 */
function generateGuideLines(
  targets: SnapTarget[],
  activeTarget: SnapTarget | null,
  position: Point2D,
  _objectWidth: number,
  _objectDepth: number,
  room: RoomConfig
): SnapGuideLine[] {
  const lines: SnapGuideLine[] = [];

  for (const target of targets) {
    const isActive = target === activeTarget;

    switch (target.type) {
      case "wall":
        // Draw line from object to wall
        lines.push({
          start: position,
          end: target.position,
          type: "wall",
          isActive,
        });
        break;

      case "corner":
        // Draw crosshair at corner
        lines.push({
          start: { x: target.position.x - 20, y: target.position.y },
          end: { x: target.position.x + 20, y: target.position.y },
          type: "corner",
          isActive,
        });
        lines.push({
          start: { x: target.position.x, y: target.position.y - 20 },
          end: { x: target.position.x, y: target.position.y + 20 },
          type: "corner",
          isActive,
        });
        break;

      case "cabinet":
        // Draw connection line between cabinets
        lines.push({
          start: position,
          end: target.position,
          type: "cabinet",
          isActive,
        });
        break;

      case "alignment":
        // Draw alignment guide across room
        if (Math.abs(target.position.x - position.x) > 0.1) {
          // Vertical alignment line
          lines.push({
            start: { x: target.position.x, y: 0 },
            end: { x: target.position.x, y: room.depth },
            type: "alignment",
            isActive,
          });
        }
        if (Math.abs(target.position.y - position.y) > 0.1) {
          // Horizontal alignment line
          lines.push({
            start: { x: 0, y: target.position.y },
            end: { x: room.width, y: target.position.y },
            type: "alignment",
            isActive,
          });
        }
        break;

      case "grid":
        // No specific guide line for grid
        break;
    }
  }

  return lines;
}

// =============================================================================
// Main Snap Calculation Function
// =============================================================================

export interface SnapCalculationParams {
  /** Current drag position in room coordinates (cm) */
  position: Point2D;
  /** Width of object being dragged (cm) */
  objectWidth: number;
  /** Depth of object being dragged (cm) */
  objectDepth: number;
  /** Room configuration */
  room: RoomConfig;
  /** Wall segments */
  walls: WallSegment[];
  /** Existing placed volumes */
  volumes: PlacedVolume[];
  /** Product catalog for dimension lookup */
  products: ProductResponse[];
  /** ID of volume being dragged (to exclude from cabinet snapping) */
  currentVolumeId?: string | null;
  /** Snap configuration */
  config?: Partial<SnapConfig>;
}

/**
 * Calculate snapped position with all snap types
 */
export function calculateSnap(params: SnapCalculationParams): SnapResult {
  const {
    position,
    objectWidth,
    objectDepth,
    room,
    walls,
    volumes,
    products,
    currentVolumeId = null,
    config: configOverrides = {},
  } = params;

  const config: SnapConfig = { ...DEFAULT_SNAP_CONFIG, ...configOverrides };
  const allTargets: SnapTarget[] = [];

  // Collect all snap targets based on enabled options
  if (config.cornerEnabled) {
    allTargets.push(
      ...findCornerSnapTargets(
        position,
        objectWidth,
        objectDepth,
        room,
        walls,
        config.cornerThreshold
      )
    );
  }

  if (config.wallEnabled) {
    allTargets.push(
      ...findWallSnapTargets(
        position,
        objectWidth,
        objectDepth,
        walls,
        config.wallThreshold
      )
    );
  }

  if (config.cabinetEnabled) {
    allTargets.push(
      ...findCabinetSnapTargets(
        position,
        objectWidth,
        objectDepth,
        currentVolumeId,
        volumes,
        products,
        config.cabinetThreshold
      )
    );
  }

  if (config.alignmentEnabled) {
    allTargets.push(
      ...findAlignmentSnapTargets(
        position,
        objectWidth,
        objectDepth,
        currentVolumeId,
        volumes,
        products,
        room,
        config.alignmentThreshold
      )
    );
  }

  // Grid snap is always calculated as fallback
  if (config.gridEnabled) {
    allTargets.push(findGridSnapTarget(position, config.gridSize));
  }

  // Sort by priority (lower = higher priority), then by distance
  allTargets.sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority - b.priority;
    }
    return a.distance - b.distance;
  });

  // Find the winning snap target (first one that's within threshold)
  let activeTarget: SnapTarget | null = null;

  for (const target of allTargets) {
    let threshold: number;

    switch (target.type) {
      case "corner":
        threshold = config.cornerThreshold;
        break;
      case "wall":
        threshold = config.wallThreshold;
        break;
      case "cabinet":
        threshold = config.cabinetThreshold;
        break;
      case "alignment":
        threshold = config.alignmentThreshold;
        break;
      case "grid":
        threshold = config.gridSize; // Always snap to grid
        break;
      default:
        threshold = 10;
    }

    if (target.distance <= threshold) {
      activeTarget = target;
      break;
    }
  }

  // Generate guide lines for visual feedback
  const guideLines = generateGuideLines(
    allTargets.filter((t) => t.type !== "grid"), // Don't show grid guides
    activeTarget,
    position,
    objectWidth,
    objectDepth,
    room
  );

  return {
    position: activeTarget?.position ?? position,
    didSnap: activeTarget !== null,
    targets: allTargets,
    activeTarget,
    guideLines,
  };
}

/**
 * Get color for snap guide line based on type
 */
export function getSnapGuideColor(type: SnapTargetType, isActive: boolean): string {
  const opacity = isActive ? 1 : 0.4;

  switch (type) {
    case "corner":
      return `rgba(34, 197, 94, ${opacity})`; // Green
    case "wall":
      return `rgba(59, 130, 246, ${opacity})`; // Blue
    case "cabinet":
      return `rgba(168, 85, 247, ${opacity})`; // Purple
    case "alignment":
      return `rgba(249, 115, 22, ${opacity})`; // Orange
    case "grid":
      return `rgba(156, 163, 175, ${opacity})`; // Gray
    default:
      return `rgba(156, 163, 175, ${opacity})`;
  }
}

/**
 * Get dash pattern for snap guide line based on type
 */
export function getSnapGuideDash(type: SnapTargetType): number[] {
  switch (type) {
    case "alignment":
      return [5, 5]; // Dashed
    case "grid":
      return [2, 2]; // Dotted
    default:
      return []; // Solid
  }
}

export default calculateSnap;
